import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { DemoWorkspaceDate, DemoWorkspaceId, Workspace } from '../constants/keys';
import { selectDate } from '../store/slices/sensors';
import type { RootState } from '../store';
import { useLocalStorage } from './useLocalStorage';

// Determine the selected date
export const useSelectedDate = (dates?: string[] | null): string => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [workspaceId] = useLocalStorage(Workspace, '');
  const { selectedDate } = useSelector((state: RootState) => state.sensors);

  const lastDate = useMemo(() => (Array.isArray(dates) && dates.length ? dates[dates.length - 1] : null), [dates]);

  const urlDateRaw = searchParams.get('date');
  const urlDate = urlDateRaw && urlDateRaw !== 'undefined' ? urlDateRaw : null;

  // 1) Init redux from URL / lastDate / demo date.
  useEffect(() => {
    if (selectedDate || !workspaceId) return;

    const nextDate = urlDate ?? (workspaceId === DemoWorkspaceId ? DemoWorkspaceDate : lastDate);

    if (nextDate) {
      dispatch(selectDate(nextDate));
    }
  }, [dispatch, selectedDate, workspaceId, urlDate, lastDate]);

  // 2) Redux synchronization -> URL (?date=...).
  useEffect(() => {
    if (!selectedDate || urlDate === selectedDate) return;

    const next = new URLSearchParams(searchParams);
    next.set('date', selectedDate);
    setSearchParams(next, { replace: true });
  }, [selectedDate, urlDate, searchParams, setSearchParams]);

  return selectedDate;
}
